import { ConditionalWrapper } from '@/components/conditional-wrapper';
import { NarratorPackGrid } from '@/components/narrator-pack-grid';
import { CH_RESOURCES_PACK } from '@/components/pack-pack-card/types';
import { PlaceholderWhileOffScreen } from '@/components/placeholder-while-offscreen';
import { GlobalFullBleed } from '@/components/ui/global-full-bleed';
import { useResources } from '@/hooks/use-resources';
import { useAnalyticsEventProps } from '@/libs/analytics/event-props-provider';
import { PackHeroVariant } from '@/utils/constants';
import { MediaPackType } from '@/utils/media/types';
import { EnumButtonName } from '@calm/evexia-analytics';
import { MediaDisplayType, MediaNestedPack } from '@calm/evexia-web-sdk';
import { CardVariant } from '@rhg/components/card/types';
import {
    defaultCarouselCardSpacing,
    defaultCarouselCardWidth,
    defaultCarouselCardWidthMobile,
} from '@rhg/components/carousel/utils';
import { ChLink } from '@rhg/components/ch-link';
import { dailyPracticeCardWidth } from '@rhg/components/discover-dailies/daily-packs';
import { ViewAllCategoryButton } from '@rhg/components/discover/view-all-category-button';
import { fullPagePadding } from '@rhg/components/full-page-container';
import {
    PackCarousel,
    PackCarouselProps,
    PackCarouselSkeleton,
    isRenderablePackItem,
} from '@rhg/components/pack-carousel';
import resourceMessages from '@rhg/components/resources/messages';
import { ROUNDED_CARD_SIZE } from '@rhg/components/rounded-card';
import { SkeletonCardVariant } from '@rhg/components/ui/skeletons';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePacks } from '@rhg/hooks/use-packs';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Button,
    Divider,
    SxProps,
    Typography,
    Theme,
    useTheme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import {
    CATEGORY_PACK,
    FEATURED_COLLECTIONS_CURATED_PACK_CLASS,
    getCardVariantFromDisplayType,
} from '@rhg/utils/pack-card';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { styleCardFooter } from '..';
import {
    PackDetailsFullWidthContainer,
    PackDetailsPaddedContainer,
    PackDetailsPaddingPx,
} from '../layout';
import { PackPageGridSection } from '../pack-page-grid-section';

export const largeButtonCardWidth = 209;
export const mobileCategoryCardWidth = 120;
export const MAX_CARDS_DISPLAYED = 5;
// Featured collections carousels show one more card than the default carousel.
export const FEATURED_COLLECTIONS_MAX_CARDS = 6;

export interface PackCarouselSectionProps {
    packClass?: string;
    title?: string;
    titleSrOnly?: boolean;
    showDivider?: boolean;
    noTitlePadding?: boolean;
    viewAllHref?: string;
    cardVariant?: CardVariant;
    skeletonType?: SkeletonCardVariant;
    cardWidth?: PackCarouselProps['itemWidth'];
    packCarouselProps?: Partial<PackCarouselProps>;
    titleSx?: SxProps<Theme>;
    showNestedTitle?: boolean;
    shouldFullBleedContent?: boolean;
    /** Number of leading cards to eager-load (next/image priority). Pass
     *  `isMobile ? 1 : 3` for an above-the-fold carousel, or 1 for a single
     *  hero. Default 0 = all lazy. */
    priorityItemCount?: number;
    /** Render this pack as a wrapping grid of cards (matching the pack detail
     *  page) instead of a carousel. Off by default so grid support stays scoped
     *  to the surfaces that want it (currently feed pages). The caller decides
     *  from its own `displayType` since the section only receives a packClass. */
    renderAsGrid?: boolean;
    /** When the pack resolves to a single renderable item, render it as one
     *  standalone 3:2 card (`CardVariant.Carousel`) instead of a carousel. Opt-in
     *  so the behavior stays scoped to the surfaces that want it (feed pages);
     *  the item count is only known here, once the pack has been fetched. */
    singleItemAsCard?: boolean;
    /** When true (default), a `banner-carousel` displayType upgrades the card to
     *  the 3:2 `carousel` variant regardless of the `cardVariant` passed. Set
     *  false on surfaces that decide "featured" some other way (feed pages treat
     *  the first pack as featured positionally) so the provided `cardVariant` is
     *  honored as-is. */
    featuredByDisplayType?: boolean;
    /** Attach per-item positional analytics props to each card (RowIndex /
     *  RowItemIndex / PackDisplayType / PackItemName / PackItemType). Opt-in —
     *  enabled on Discover/Feed, which install the ambient row provider. */
    trackItemPositions?: boolean;
    /** Derive "view all" visibility from the pack's contents instead of always
     *  showing it whenever a `viewAllHref` is present. Uncapped packs (rounded
     *  carousel, goals row, snappy quick-nav) render everything they have, so
     *  they never get a "view all". Capped packs show it when the pack handed
     *  us more items than we render, or it flags more beyond what it returned
     *  (`hasMore`). Opt-in for feed details, where a pack that already shows
     *  everything shouldn't advertise a "view all"; Discover keeps the
     *  always-on behavior. */
    viewAllFromItemCount?: boolean;
    /** Called after render when the pack is fetched but has no displayable
     *  items. */
    onEmpty?: () => void;
    /** Forwarded to every item's card so a click can append it to the
     *  destination pack/feed link (see `PackHeroVariant`). Opt-in — only rows
     *  that want an alternate destination hero treatment set this. */
    heroVariant?: PackHeroVariant;
}

export const styleTitleRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: (theme: Theme) => theme.ds.spacingUnits.HEADING_CONTENT_DESKTOP,
};

const styleMobileCarouselException = {
    gridTemplateRows: '1fr 1fr',
    gridAutoFlow: 'column',
    pr: (theme: Theme) => `${theme.semantics.Spacing[16]}px`,
    ':before': {
        content: 'none',
    },
};

// `PackDetailsPaddingPx` holds the page gutter as raw pixel *numbers* (e.g. 16 /
// 32). MUI's `pl`/`pr` shorthands multiply a bare number by the theme spacing
// base (4px here), so `pl: 16` resolves to 64px, not 16px. Serialize to explicit
// px strings so the goals grid's left padding equals the gutter. (This was the
// goals-row indent that only showed in Safari: on Chrome the carousel's
// `scroll-snap-type: x mandatory` snaps the first card to the 16px
// `scroll-padding-left` on load, absorbing the extra padding; Safari disables
// scroll-snap — see `isSafari` in ../../carousel — so the 4x padding stayed
// visible.)
const packDetailsPaddingLeftPx = Object.fromEntries(
    Object.entries(PackDetailsPaddingPx).map(([breakpoint, value]) => [
        breakpoint,
        `${value}px`,
    ])
);

// Featured collections on mobile is a static 3×2 grid that fits the viewport
// (no horizontal scroll) rather than the 2-row scrolling grid the other
// cap-exempt packs use. Two equal columns fill the viewport width; six capped
// items wrap to three rows.
const styleMobileFeaturedCollectionsGrid = {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'none',
    gridAutoFlow: 'row',
    overflowX: 'visible',
    // Match the section heading's horizontal padding so the cards left-align to
    // it.
    px: fullPagePadding,
    // Let each card fill its column instead of the fixed carousel item width
    // (same relaxation getListStyle applies to the quick-nav mobile grid).
    '& .MuiListItem-root': {
        minWidth: 'auto',
        maxWidth: 'none',
    },
    // Drop the FullscreenCarousel leading-offset spacer column.
    ':before': { content: 'none' },
};

const styleList = {
    '&.MuiList-root.MuiList-root': {
        maxWidth: 'max-content',
    },
};
const stylePackCarouselTitle = {
    mb: 2,
};

const styleSectionReponsiveDivider = {
    mt: (theme: Theme) => theme.ds.spacingUnits.SECTION,
    mb: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.CONTENT,
        md: theme.ds.spacingUnits.SECTION,
    }),
};

const RESOURCES_PACK_ITEM_POSITION = 1; // Position where the resources pack item should be inserted
export const discoverCategoriesPackClassPrefix =
    'calm-health-discover-categories';

const ConditionalTitlePadding = ({
    noTitlePadding,
    children,
}: PropsWithChildren<{ noTitlePadding?: boolean }>) =>
    noTitlePadding ? (
        children
    ) : (
        <PackDetailsPaddedContainer>{children}</PackDetailsPaddedContainer>
    );

/**
 * Wraps the carousel content. A single standalone card is aligned with the
 * section title — padded to the page gutter, or (when the title opts out of
 * padding, e.g. feed rows where the ambient grid layout already supplies the
 * gutter) left unpadded so it doesn't double-indent past the heading. A
 * carousel is full-width so it can bleed to the screen edges.
 */
const PackCarouselContentContainer = ({
    isSingleItemCard,
    noTitlePadding,
    children,
}: PropsWithChildren<{
    isSingleItemCard?: boolean;
    noTitlePadding?: boolean;
}>) =>
    isSingleItemCard ? (
        <ConditionalTitlePadding noTitlePadding={noTitlePadding}>
            {children}
        </ConditionalTitlePadding>
    ) : (
        <PackDetailsFullWidthContainer>
            {children}
        </PackDetailsFullWidthContainer>
    );

export const PackCarouselSection = ({
    packClass,
    title,
    titleSrOnly,
    showDivider = true,
    viewAllHref,
    cardWidth,
    cardVariant: providedCardVariant = 'carousel',
    skeletonType = 'daily',
    packCarouselProps,
    noTitlePadding,
    titleSx,
    showNestedTitle,
    shouldFullBleedContent = false,
    priorityItemCount = 0,
    renderAsGrid = false,
    singleItemAsCard = false,
    featuredByDisplayType = true,
    trackItemPositions = false,
    viewAllFromItemCount = false,
    onEmpty,
    heroVariant,
}: PackCarouselSectionProps) => {
    const currentBreakpoint = useCurrentBreakpointName();
    const { activeCircleId } = useActiveCircleId();
    const { isFetched, packs } = usePacks([packClass ?? ''], activeCircleId);
    const { formatMessage } = useIntl();
    const ambientProps = useAnalyticsEventProps();
    const isMobile = useIsMobile();
    const theme = useTheme();
    const { resources } = useResources();
    const ogPack = packs?.[0];
    const isDiscoverCategoriesPack = ogPack?.packClass.startsWith(
        discoverCategoriesPackClassPrefix
    );
    const nestedTitle = ogPack?.title;
    const isQuickNavCategory = ogPack?.displayType === CATEGORY_PACK;
    const isMobileQuickNav = isMobile && isQuickNavCategory;
    const isRoundedCarousel =
        ogPack?.displayType === MediaDisplayType.RoundedCarousel;
    // Narrator packs (e.g. the "Artists" music pack) render as a grid of
    // circular cards rather than a carousel — see the early return below, which
    // uses `ogPack` directly so it keeps all its items (no `MAX_CARDS_DISPLAYED`
    // slice). Kept out of `skipMaxCardLimit` so the alias narrowing that keeps
    // `pack` defined below still holds.
    const isNarratorPack = ogPack?.items?.some(
        (item) => item?.type === 'narrator'
    );
    const isFeaturedCollections =
        ogPack?.packClass === FEATURED_COLLECTIONS_CURATED_PACK_CLASS;
    // `featured-collections-curated` arrives as `grid` but renders with the
    // goals-row layout, so it stays in `skipMaxCardLimit` for the mobile layout
    // branches below. Keep every disjunct keyed off `ogPack` so
    // `skipMaxCardLimit` still narrows `ogPack` to defined below.
    const skipMaxCardLimit =
        isQuickNavCategory ||
        ogPack?.displayType === MediaDisplayType.CalmHealthGoalsRowCards ||
        isFeaturedCollections ||
        ogPack?.displayType === MediaDisplayType.RoundedCarousel;
    // Unlike the other cap-exempt packs, featured collections is still capped —
    // it only opts out of the layout the flag also drives, not the item limit.
    // Its cap is FEATURED_COLLECTIONS_MAX_CARDS rather than the default
    // MAX_CARDS_DISPLAYED used by every other carousel.
    const capItemsToMax = !skipMaxCardLimit || isFeaturedCollections;
    const cardLimit = isFeaturedCollections
        ? FEATURED_COLLECTIONS_MAX_CARDS
        : MAX_CARDS_DISPLAYED;
    const pack = useMemo(
        () =>
            capItemsToMax
                ? ({
                      ...ogPack,
                      items: ogPack?.items?.slice(0, cardLimit),
                  } as MediaPackType)
                : ogPack,
        [ogPack, capItemsToMax, cardLimit]
    );

    // Narrator/grid/link/quick-nav-category packs each have their own
    // rendering path below with no title/divider chrome tied to item count
    // (quick-nav category rows can also gain a resources item injected further
    // down), so they're excluded from the empty-section check — an empty
    // `items` array doesn't mean "nothing to show" for any of them.
    const isStandardSection =
        !isNarratorPack &&
        !renderAsGrid &&
        !isQuickNavCategory &&
        pack?.displayType !== 'link';
    const hasRenderableItems = (pack?.items ?? []).some((item) =>
        isRenderablePackItem(item, {
            allowPackOfPacks: true,
            allowFeedItems: true,
        })
    );
    const isEmpty = isFetched && isStandardSection && !hasRenderableItems;

    useEffect(() => {
        if (isEmpty) {
            onEmpty?.();
        }
    }, [isEmpty, onEmpty]);

    if (isEmpty) {
        return null;
    }

    // Narrator packs render as a wrapping grid of circular cards (like the
    // `/discover/pack/[packClass]` grid) rather than a horizontal carousel. Use
    // `ogPack` so every narrator is shown, not the `MAX_CARDS_DISPLAYED` slice.
    if (isNarratorPack) {
        return <NarratorPackGrid pack={ogPack} />;
    }

    // Grid packs render as a wrapping grid of cards (matching the pack detail
    // page — same `GlobalGridItem` sizing) with no section title/view-all,
    // rather than a carousel. Uses `ogPack` so every item is shown. The caller
    // decides (`renderAsGrid`) — feed details drives this from the fetched
    // pack's item composition. Wait for the pack to resolve before rendering:
    // `renderAsGrid` fires on every render, so without this guard an empty grid
    // flashes while `usePacks` is still loading (`ogPack` undefined).
    if (renderAsGrid) {
        if (!ogPack) return null;
        return <PackPageGridSection pack={ogPack} />;
    }

    // this whole section is temporary and will be removed when bff is ready.
    // at that point, the resources pack item will be added to the pack directly
    // in content admin and filtered out in the bff if resources are not
    // available for the user.
    const resourcesTitle = formatMessage(resourceMessages['resources.title']);
    if (
        isDiscoverCategoriesPack &&
        isQuickNavCategory &&
        resources?.length &&
        !pack.items.some((item) => item.title === resourcesTitle)
    ) {
        const resourcesPackItem: (typeof pack.items)[number] = {
            displayType: 'carousel',
            packClass: CH_RESOURCES_PACK,
            title: resourcesTitle,
            image: {
                contentType: 'image/png',
                id: '3d2a804c8a5824df6f16081a6e6afb13',
                size: 1096,
                type: 'image',
                url: 'https://assets.calm.com/3d2a804c8a5824df6f16081a6e6afb13.png',
            },
            type: 'pack',
        };
        pack.items = [
            ...pack.items.slice(0, RESOURCES_PACK_ITEM_POSITION),
            resourcesPackItem,
            ...pack.items.slice(RESOURCES_PACK_ITEM_POSITION),
        ];
    }

    const isViewAllLink = pack?.displayType === 'link';
    if (isViewAllLink) {
        const isPackOfPacks =
            pack?.items?.every(({ type }) => type === 'pack') ?? false;
        const innerPack = pack?.items[0] as MediaNestedPack;
        return (
            <ViewAllCategoryButton
                label={
                    isPackOfPacks && innerPack?.title
                        ? innerPack?.title
                        : title ?? ''
                }
                packClass={
                    isPackOfPacks && innerPack?.packClass
                        ? innerPack?.packClass
                        : packClass ?? ''
                }
            />
        );
    }

    const isFeatured =
        featuredByDisplayType && pack?.displayType === 'banner-carousel';
    const isGoalCards =
        pack?.displayType === MediaDisplayType.CalmHealthGoalsRowCards;

    // A pack that resolves to a single renderable item is shown as one
    // standalone 3:2 card rather than a carousel (see PackCarousel's
    // `singleItemAsCard`). Uses the shared `isRenderablePackItem` predicate with
    // the same `allowPackOfPacks`/`allowFeedItems` flags this section passes to
    // `<PackCarousel>` below, so the count can't diverge from what renders.
    const renderableItemCount = (pack?.items ?? []).filter((item) =>
        isRenderablePackItem(item, {
            allowPackOfPacks: true,
            allowFeedItems: true,
        })
    ).length;
    const isSingleItemCard = singleItemAsCard && renderableItemCount === 1;

    const cardVariant = isFeatured
        ? getCardVariantFromDisplayType(pack?.displayType)
        : isSingleItemCard
        ? CardVariant.Carousel
        : providedCardVariant;
    // 3:2 cards (featured collections and single-item packs) use the wider
    // carousel card width; 1:1 (carouselSquare) cards keep the daily width.
    const isThreeByTwoCard = cardVariant === CardVariant.Carousel;
    const cardWidthFallback = isGoalCards
        ? largeButtonCardWidth
        : isThreeByTwoCard
        ? isMobile
            ? defaultCarouselCardWidthMobile
            : defaultCarouselCardWidth
        : dailyPracticeCardWidth;

    let itemWidth = cardWidth ?? cardWidthFallback;
    if (isRoundedCarousel) {
        // Fixed circular cards — own the sizing regardless of caller-provided
        // cardWidth so every surface (discover, /pack, /feed) renders alike.
        itemWidth = ROUNDED_CARD_SIZE;
    } else if (isMobile && skipMaxCardLimit) {
        itemWidth = isQuickNavCategory
            ? mobileCategoryCardWidth
            : cardWidthFallback;
    }
    const effectiveSkeletonType = isRoundedCarousel ? 'rounded' : skeletonType;
    // The heading actually rendered for this section (see the Typography
    // below) — reused as the "View All" button's aria-label context so its
    // accessible name identifies which section it belongs to (WCAG 2.4.4).
    const effectiveTitle = title ?? nestedTitle;
    const displayTitle =
        (title || (showNestedTitle && nestedTitle)) && !titleSrOnly;
    const itemSpacing = isMobileQuickNav
        ? theme.semantics.Spacing[16]
        : defaultCarouselCardSpacing;
    // A single card shows everything the pack has, so there is nothing to
    // "view all" — hide the link in that case.
    //
    // When `viewAllFromItemCount` is opted into (feed details), only advertise
    // "view all" when there's genuinely more to see. An uncapped pack (rounded
    // carousel, goals row, snappy quick-nav) renders every item it has, so it
    // never gets a "view all" — this is the design spec's explicit "no View
    // All" set. A capped pack shows the link when it handed us more items than
    // we render (`pack` is the possibly-sliced view of `ogPack`), or it flags
    // that more exist beyond what it returned (`hasMore`). Everywhere else
    // (Discover) keeps showing the link whenever a href is present.
    const shownItemCount = pack?.items?.length ?? 0;
    const givenItemCount = ogPack?.items?.length ?? 0;
    const hasMoreToView =
        capItemsToMax && (givenItemCount > shownItemCount || !!ogPack?.hasMore);
    const showViewAll =
        !!viewAllHref &&
        !isSingleItemCard &&
        (!viewAllFromItemCount || hasMoreToView);

    // "View all" on a pack within a collection/feed page, or on the featured
    // collections section, is a distinct interaction from a category/goal/topic
    // "view all". Feed pages install an ambient FeedID; the featured collections
    // section is keyed by its curated pack class. Everything else keeps the
    // existing ViewAllCategory button name.
    const isCollectionViewAll = !!ambientProps.FeedID || isFeaturedCollections;
    const viewAllTrackButton = isCollectionViewAll
        ? EnumButtonName.DiscoverCarouselViewAll
        : EnumButtonName.ViewAllCategory;

    return (
        <>
            {(displayTitle || showViewAll) && (
                <ConditionalTitlePadding noTitlePadding={noTitlePadding}>
                    <Box sx={sxAppend(styleTitleRow, titleSx)}>
                        {displayTitle && (
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={stylePackCarouselTitle}
                            >
                                {effectiveTitle}
                            </Typography>
                        )}
                        {showViewAll && (
                            <Button
                                component={ChLink}
                                href={viewAllHref}
                                trackButton={viewAllTrackButton}
                                trackProps={{ PackClass: packClass }}
                                color="primary"
                                size="medium"
                                variant="text"
                                aria-label={
                                    effectiveTitle
                                        ? formatMessage(
                                              commonMessages[
                                                  'common.view_all_section'
                                              ],
                                              { sectionTitle: effectiveTitle }
                                          )
                                        : undefined
                                }
                            >
                                {formatMessage(
                                    commonMessages['common.view_all']
                                )}
                            </Button>
                        )}
                    </Box>
                </ConditionalTitlePadding>
            )}
            {title && titleSrOnly && (
                <Typography variant="srOnly" component="h2">
                    {title}
                </Typography>
            )}
            <ConditionalWrapper
                // A single-item pack renders as one standalone card, so it lives
                // in the padded content container (aligned with the title)
                // rather than the full-bleed carousel container.
                condition={!isSingleItemCard && shouldFullBleedContent}
                Wrapper={GlobalFullBleed}
            >
                <PackCarouselContentContainer
                    isSingleItemCard={isSingleItemCard}
                    noTitlePadding={noTitlePadding}
                >
                    <PlaceholderWhileOffScreen
                        placeholder={
                            <PackCarouselSkeleton
                                skeletonType={effectiveSkeletonType}
                                itemWidth={itemWidth}
                                itemSpacing={itemSpacing}
                                count={isSingleItemCard ? 1 : undefined}
                            />
                        }
                    >
                        <PackCarousel
                            isFullscreen
                            data-testid="PackCarousel"
                            pack={pack}
                            cardVariant={cardVariant}
                            singleItemAsCard={isSingleItemCard}
                            itemWidth={itemWidth}
                            itemSpacing={itemSpacing}
                            firstItemOffsetPx={PackDetailsPaddingPx}
                            trackBackButton={
                                EnumButtonName.DiscoverCarouselBack
                            }
                            trackForwardButton={
                                EnumButtonName.DiscoverCarouselForward
                            }
                            cardContentSx={styleCardFooter}
                            isFetched={isFetched}
                            skeletonType={effectiveSkeletonType}
                            allowPackOfPacks
                            allowFeedItems
                            priorityItemCount={priorityItemCount}
                            trackItemPositions={trackItemPositions}
                            heroVariant={heroVariant}
                            {...packCarouselProps}
                            listSx={sxAppend(
                                isQuickNavCategory && currentBreakpoint === 'xl'
                                    ? styleList
                                    : {},
                                // Featured collections on mobile: static 3×2
                                // grid applied directly to the carousel list.
                                isMobile && isFeaturedCollections
                                    ? styleMobileFeaturedCollectionsGrid
                                    : {},
                                packCarouselProps?.listSx
                            )}
                            sx={sxAppend(
                                // The 2-row mobile grid is for the quick-nav /
                                // goals category layouts. A rounded carousel is
                                // a standard single-row scroll, so it skips the
                                // card limit without inheriting this layout.
                                // Featured collections opts out here — it gets
                                // its own static 3×2 grid via `listSx` below.
                                skipMaxCardLimit &&
                                    isMobile &&
                                    !isRoundedCarousel &&
                                    !isFeaturedCollections
                                    ? {
                                          '& .MuiList-root': {
                                              ...styleMobileCarouselException,
                                              gridTemplateColumns: `repeat(${Math.ceil(
                                                  pack?.items?.length / 2
                                              )}, minmax(${
                                                  isQuickNavCategory
                                                      ? mobileCategoryCardWidth
                                                      : cardWidthFallback
                                              }px, 1fr))`,
                                              pl: isQuickNavCategory
                                                  ? `${theme.semantics.Spacing[16]}px`
                                                  : packDetailsPaddingLeftPx,
                                          },
                                      }
                                    : {},
                                packCarouselProps?.sx
                            )}
                        />
                    </PlaceholderWhileOffScreen>
                </PackCarouselContentContainer>
            </ConditionalWrapper>
            {showDivider && (
                <PackDetailsPaddedContainer>
                    <Divider sx={styleSectionReponsiveDivider} />
                </PackDetailsPaddedContainer>
            )}
        </>
    );
};
